import { Fragment, memo } from "react";

import { BodySmall, BodyXS, Box, Button, H5 } from "@parspec/pixel";

import { environment } from "src/environments";
import { callProxyURL } from "src/features/BOM/FinalDocuments/utils/downloadFile";
import { SummaryData } from "./types";
import { SUMMARY_MODAL } from "./constant";

interface ResolveConflictModalProps {
    data: SummaryData;
    errorMessage?: string;
}

const sxStyles = {
    bodySmall: { lineHeight: '1.25rem' },
    ul: { listStylePosition: "inside" },
    li: { "::marker": { color: "secondary.main" } },
    liText: { lineHeight: '1.25rem', display: 'inline' },
    countTitle: { lineHeight: "1.25rem", fontWeight: 500 },
};

const DISCARD_CHECK_FIELDS = ["CRM ID", "ERP ID", "DUNS #"];

const ImportSummaryContent = memo(function ImportSummaryContent(props: ResolveConflictModalProps) {
    const { data, errorMessage } = props;
    const {
        new_companies,
        new_contacts,
        update_companies,
        update_contacts,
        discarded_rows_count: discardedRowsCount,
        discarded_rows_csv: discardedRowsCsvUrl,
    } = data;

    const downloadDiscardedItems = () => {
        if (!discardedRowsCsvUrl) return;
        callProxyURL(`${environment.j}${discardedRowsCsvUrl}`, "csv", "discarded-rows.csv");
    };

    const countItems = [{
        title: new_companies.length === 1 ? "New Company" : "New Companies",
        count: new_companies.length,
        countColor: 'success.main',
    }, {
        title: new_contacts.length === 1 ? "New Contact" : "New Contacts",
        count: new_contacts.length,
        countColor: 'success.main',
    }, {
        title: update_companies.length === 1 ? "Updated Company" : "Updated Companies",
        count: update_companies.length,
        countColor: 'tertiary.dark',
    }, {
        title: update_contacts.length === 1 ? "Updated Contact" : "Updated Contacts",
        count: update_contacts.length,
        countColor: 'tertiary.dark',
    }, {
        title: 'Discarded',
        count: discardedRowsCount,
        countColor: 'error.main',
    }].filter(item => !!item.count);

    return (
        <Box width={710}>
            <Box display="flex" flexDirection="column" gap={4}>
                <Box display="flex" width="100%" justifyContent="space-evenly">
                    {countItems.length ? countItems.map((item, idx) => {
                        const { title, count, countColor } = item;
                        const addRightDivider = idx !== countItems.length - 1;
                        return (
                            <Fragment key={title}>
                                <Box display="flex" flexDirection="column" padding={2} alignItems="center" justifyContent="center" flex="0 1 auto">
                                    <H5 color={countColor} limit={false}>
                                        {count}
                                    </H5>
                                    <BodyXS
                                        sx={sxStyles.countTitle}
                                        textTransform="uppercase"
                                        limit={false}
                                        color="secondary.main"
                                    >
                                        {title}
                                    </BodyXS>
                                </Box>
                                {addRightDivider && (
                                    <Box width={0} border="1px solid" borderColor="neutral.main" />
                                )}
                            </Fragment>
                        );
                    }) : (
                        <BodySmall limit={false} color="secondary.main" sx={sxStyles.bodySmall}>
                            {SUMMARY_MODAL.EMPTY_SUMMARY_TEXT}
                        </BodySmall>
                    )}
                </Box>

                {!!discardedRowsCount && (
                    <Box display="flex" flexDirection="column" gap={4}>
                        <Box>
                            <BodySmall limit={false} color="secondary.main" sx={sxStyles.bodySmall}>
                                {SUMMARY_MODAL.DISCARDED_TEXT}
                            </BodySmall>
                            <Box component="ul" sx={sxStyles.ul}>
                                {DISCARD_CHECK_FIELDS.map(item => (
                                    <Box key={item} component="li" sx={sxStyles.li}>
                                        <BodySmall limit={false} color="secondary.main" sx={sxStyles.liText}>
                                            {item}
                                        </BodySmall>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={2} alignItems="flex-start">
                            <BodySmall sx={sxStyles.bodySmall}>
                                {SUMMARY_MODAL.DISCARDED_SUB_TEXT}
                            </BodySmall>

                            <Button color="secondary" variant="outlined" onClick={downloadDiscardedItems}>
                                Download Discarded Items
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
            {!!errorMessage && (
                <BodySmall limit={false} color="error.main" mt={3}>
                    {errorMessage}
                </BodySmall>
            )}
        </Box>
    )
});

export default ImportSummaryContent;